import React, { useMemo, useState } from 'react'
import { Link, withRouter } from 'react-router-dom'

import ProfilePicture from 'components/profile/profile-picture'

import {
    formatByTimeZone,
    getApplicableUsers
} from 'utils'

import { DragDropContext, Droppable } from 'react-beautiful-dnd'
import Milestone from 'sections/projects/project/project-tasks/milestone/milestone'
import AlertBanner from 'components/alerts/alert-banner'

import copy from 'fast-copy'

import moment from 'moment'

export default withRouter(({
    user,
    isOnline,
    startMoment,
    endMoment,
    allTasks,
    allTeams,
    allProjects,
    allUsers,
    currentAccount,
    zIndex,
    location,
    taskUpdateCallback
}) => {
    const {
        first_name,
        last_name,
        id,
        tasks = []
    } = user

    const tasksToShow = (() => {
        const _tasksToShow = []

        tasks.forEach(taskId => {
            let task = copy(allTasks[taskId])

            if (!task) {
                return
            }

            const parentProject = allProjects[task.project_id]

            if (!parentProject) {
                return
            }

            const taskColumns = parentProject.columns

            const dateColumnIfExists = taskColumns.filter(Boolean).find(column => column.type === 'DATE' && column.is_primary)

            if (!dateColumnIfExists) {
                return
            }

            const dateValueIfExists = task.column_data ? copy(task.column_data[dateColumnIfExists.id]) : null

            if (!dateValueIfExists || !dateValueIfExists.end) {
                return
            }

            if (task.is_recurring) {
                const recurredTaskDatesDelta = (() => {
                    const {
                        recurring_anchor_date,
                        recurring_incrementer,
                        recurring_unit
                    } = task

                    const originalRecurringAnchorMoment = moment(dateValueIfExists.end)

                    const recurringAnchorMoment = moment(originalRecurringAnchorMoment)

                    let recurringUnitStr = 'days'

                    switch (recurring_unit) {
                        case 2:
                            recurringUnitStr = 'weeks'
                            break
                        case 3:
                            recurringUnitStr = 'months'
                            break
                    }

                    if (recurringAnchorMoment.isBefore(startMoment)) {
                        let hasGottenPastThisWeek = false

                        while (!hasGottenPastThisWeek) {
                            recurringAnchorMoment.add(parseInt(recurring_incrementer), recurringUnitStr)

                            if (recurringAnchorMoment.isAfter(startMoment) && recurringAnchorMoment.isBefore(endMoment)) {
                                return recurringAnchorMoment.diff(originalRecurringAnchorMoment, 'days')
                            } else if (recurringAnchorMoment.isAfter(endMoment)) {
                                hasGottenPastThisWeek = true
                            }
                        }
                    }
                })()

                if (recurredTaskDatesDelta) {
                    task = copy(task)
                    task.isRecurredDuplicate = true
                    task.recurrenceDeltaDays = recurredTaskDatesDelta

                    if(dateValueIfExists.start){
                        dateValueIfExists.start = moment(dateValueIfExists.start).add(recurredTaskDatesDelta, 'days').format('YYYY-MM-DD')
                    }

                    dateValueIfExists.end = moment(dateValueIfExists.end).add(recurredTaskDatesDelta, 'days').format('YYYY-MM-DD')
                }
            }

            const applicableUsers = getApplicableUsers(currentAccount, allUsers, parentProject, allTeams)

            task.applicableUserIds = applicableUsers.map(_user => (_user ? _user.id : null))

            const dateMoment = formatByTimeZone(dateValueIfExists.end, null, true)

            task.temp_end_date = dateValueIfExists.end
            task.temp_start_date = dateValueIfExists.start

            task.parentColumns = taskColumns

            if (dateMoment.isAfter(startMoment) && dateMoment.isBefore(endMoment)) {
                const peopleColumnId = task.parentColumns.find(column => column.type === 'PEOPLE' && column.is_primary).id

                const peopleColumnData = task.column_data[peopleColumnId]

                if (peopleColumnData && peopleColumnData.indexOf(user.id) !== -1) {
                    _tasksToShow.push(task)
                }
            }
        })

        _tasksToShow.sort((a, b) => {
            const aStart = a.temp_start_date || ''
            const bStart = b.temp_start_date || ''
            return aStart.localeCompare(bStart)
        })

        _tasksToShow.sort((a, b) => {
            return a.temp_end_date.localeCompare(b.temp_end_date)
        })

        return _tasksToShow
    })()

    const {
        totalHours,
        hoursWorked
    } = useMemo(() => {
        const totalHours = tasksToShow.reduce((total, task) => {
            return total + (task.estimated_minutes / 60)
        }, 0)

        return {
            totalHours,
            hoursWorked: (user.hours_worked && user.hours_worked[currentAccount.id]) ? user.hours_worked[currentAccount.id] : 38
        }
    }, [tasksToShow])

    const noop = () => { }

    const [currentOpenColumnId, setCurrentOpenColumnId] = useState(null)
    const [currentFocusedTask, setCurrentFocusedTask] = useState(null)
    const [currentEditedTask, setCurrentEditedTask] = useState(null)

    const editTaskName = id => {
        setCurrentEditedTask(id)
    }

    const openMenu = (type, id, focusOnly = false) => {
        setCurrentOpenColumnId(!focusOnly ? type : null)
        setCurrentFocusedTask(id)
    }

    const isOverencumbered = hoursWorked < totalHours

    const hoursDelta = Math.round((hoursWorked - totalHours) * 10) / 10

    const activeProjectsUrl = (() => {
        const urlSegments = location.pathname.split('/')

        return `/${urlSegments[1]}/people/staff/${user.id}/details`
    })()

    return (
        <li
            className={'team-member'}
            key={id}
            style={{
                zIndex
            }}
        >
            <div className={'user-info'}>
                <Link className={'profile-picture-parent tooltip-trigger'} to={activeProjectsUrl}>
                    <ProfilePicture
                        user={user}
                    />
                    <div className="tooltip">
                        View active projects
                    </div>
                </Link>
                <h2>
                    {isOnline &&
                        <em className={'online-dot visible'} />
                    }
                    {first_name} {last_name}
                </h2>
                <div className={`dateprogress-bar ${isOverencumbered ? 'red' : ''}`}>
                    <div style={{
                        width: ((totalHours / hoursWorked) * 100) + '%'
                    }} />
                </div>
                <span className={'availability-text'}>
                    {isOverencumbered ? `Overassigned by ${Math.abs(hoursDelta)} hours` : `${hoursDelta} hours free this week`}
                </span>

            </div>
            <div className={'tasks'}>
                <DragDropContext onDragStart={noop} onDragEnd={noop}>
                    <Droppable droppableId={'tasks'} type="MILESTONE">
                        {provided => (
                            <ul ref={provided.innerRef} className={'milestones-list'}>
                                { tasksToShow.length ?
                                    <Milestone
                                        isCapacity={true}
                                        accountData={null}
                                        addTaskRef={noop}
                                        addMilestoneRef={noop}
                                        applicableUsers={[]}
                                        areMilestonesReordering={false}
                                        clearTaskActivityHistory={noop}
                                        collapseCallback={noop}
                                        currentProject={null}
                                        deleteMilestone={noop}
                                        editTaskName={editTaskName}
                                        forceAddVisibleId={noop}
                                        forceSetCurrentFocusedTask={noop}
                                        handleInputFocus={noop}
                                        visibleIds={[]}
                                        isOpen={true}
                                        isPremium={true}
                                        menuState={{
                                            currentOpenColumnId,
                                            currentFocusedTask
                                        }}
                                        mobileSpacerHeight={0}
                                        currentEditedTask={currentEditedTask}
                                        currentFocusedTask={currentFocusedTask}
                                        milestone={{
                                            id: -1,
                                            name: '',
                                            tasks: tasksToShow
                                        }}
                                        milestoneIndex={0}
                                        openMenu={openMenu}
                                        project={null}
                                        restoreMilestone={noop}
                                        roles={{}}
                                        searchTerm={''}
                                        setSpacerHeight={noop}
                                        taskActivityHistory={[]}
                                        taskUpdateCallback={taskUpdateCallback}
                                        transitionDelayStartIndex={0}
                                        zIndex={1}
                                    />
                                    :
                                    <AlertBanner
                                        title={`${user.first_name} has no assigned tasks this week.`}
                                        body={`Assign them tasks from a project, and they'll show up here.`}
                                    />
                                }
                            </ul>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </li>
    )
})